import React, { useContext } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import InputDateRange from '@atom/components/common/inputDateRange/InputDateRange';
import WorkOrdersContext, {
  FilterSection,
  WorkOrdersInputActionTypes,
} from '@atom/components/workOrders/WorkOrdersContext';
import { WorkOrdersInputState } from '@atom/hooks/useWorkOrdersFilters';
import { Icon, IconButton, Select, TextField } from '@atom/mui';
import {
  KeyboardArrowDownIcon,
  KeyboardArrowUpIcon,
  WorkIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { WorkOrdersConnectionInput } from '@atom/types/work';
import { WorkDatePresetKey } from '@atom/types/workFilters';
import { toggleFromSet } from '@atom/utilities/setUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';
import workOrderPriorityUtilities from '@atom/utilities/workOrderPriorityUtilities';
import {
  convertRangeDateToMillis,
  DateRange,
  DateRangePresetOption,
  DateRangeTerminator,
} from '@atom/utilities/workOrdersDateFilterUtilities';
import { workOrderStatusTypes } from '@atom/utilities/workOrderStatusUtilities';

import WorkOrdersTemplateFilter from './WorkOrdersTemplateFilter';
import WorkOrdersTemplateFolderFilter from './WorkOrdersTemplateFolderFilter';
import WorkOrdersWorkFieldsFilter from './WorkOrdersWorkFieldsFilter';

import classes from './workOrdersFilters.module.css';

const { MenuItem } = Select;

const DAY_IN_MILLISECONDS = 8.64e7;

const styles = {
  icon: {
    marginRight: '1rem',
  },
  separator: {
    margin: '0 1rem',
    paddingBottom: '0.5rem',
  },
};

const inputLabelSx = {
  color: `${colors.neutral.dim} !important`,
};

const WorkOrdersFiltersWorkSection = () => {
  const {
    collapsedSections,
    setCollapsedSections,
    workOrdersInputCart,
    dispatch,
    filtersDisabled,
  } = useContext(WorkOrdersContext);

  const { dateFilterPresetsCart } = workOrdersInputCart;

  const onToggle = (section: FilterSection) => {
    setCollapsedSections(toggleFromSet(collapsedSections, section));
  };

  const handleUpdate = (value: any, property: keyof WorkOrdersInputState) => {
    dispatch({
      type: WorkOrdersInputActionTypes.UPDATE_WORK_ORDERS_INPUT_PROPERTY,
      data: {
        property,
        value,
      },
    });
  };

  const handleDateChange =
    (
      property: keyof WorkOrdersConnectionInput,
      terminator: DateRangeTerminator,
      preset?: DateRange | string,
    ) =>
    (val: Date) => {
      handleUpdate(convertRangeDateToMillis(val, terminator, preset), property);
    };

  const handleDatePresetChange =
    (type: WorkDatePresetKey, property: string) =>
    (option: DateRangePresetOption) => {
      const newDateFilterPresets = {
        ...dateFilterPresetsCart,
        [type]: {
          ...dateFilterPresetsCart[type],
          [property]: option.value,
        },
      };
      handleUpdate(newDateFilterPresets, 'dateFilterPresetsCart');
    };

  const handleDurationChange =
    (property: keyof WorkOrdersConnectionInput) =>
    (event: React.ChangeEvent<any>) => {
      const value =
        event.target.value === ''
          ? null
          : Number(event.target.value) * DAY_IN_MILLISECONDS;

      handleUpdate(value, property);
    };

  const handleMultiSelectChange =
    (property: keyof WorkOrdersConnectionInput) =>
    (event: React.ChangeEvent<any>) => {
      handleUpdate(event.target.value, property);
    };

  const getDateValue = (property: keyof WorkOrdersConnectionInput) => {
    if (workOrdersInputCart && !isNilOrEmpty(workOrdersInputCart[property])) {
      return new Date(Number(workOrdersInputCart[property]));
    }

    return null;
  };

  const convertMillisecondsToDays = (daysMillis?: number) => {
    return daysMillis ? daysMillis / DAY_IN_MILLISECONDS : daysMillis;
  };

  const handleNoDueDateSelect = (selected: boolean) => {
    handleUpdate(selected, 'withoutDueDate');
  };

  return (
    <div className={classes.filtersSection}>
      <div className={classes.filtersSectionHeader}>
        <div>
          <Icon style={styles.icon} icon={WorkIcon} />
          Work
        </div>
        <IconButton onClick={() => onToggle(FilterSection.WORK)} edge="end">
          <Icon
            icon={
              collapsedSections.has(FilterSection.WORK)
                ? KeyboardArrowDownIcon
                : KeyboardArrowUpIcon
            }
          />
        </IconButton>
      </div>
      {!collapsedSections.has(FilterSection.WORK) && (
        <div className={classes.filtersSectionContent}>
          <div className={classes.filterContainer}>
            <InputDateRange
              label="Due Date"
              disabled={filtersDisabled}
              presetSelected={
                dateFilterPresetsCart?.[WorkDatePresetKey.WORK]?.dueDatePreset
              }
              startDateValue={getDateValue('dueDateStart')}
              handleStartChange={handleDateChange(
                'dueDateStart',
                DateRangeTerminator.START,
                dateFilterPresetsCart?.[WorkDatePresetKey.WORK]?.dueDatePreset,
              )}
              endDateValue={getDateValue('dueDateEnd')}
              handleEndChange={handleDateChange(
                'dueDateEnd',
                DateRangeTerminator.END,
                dateFilterPresetsCart?.[WorkDatePresetKey.WORK]?.dueDatePreset,
              )}
              handleNoDueDateSelect={handleNoDueDateSelect}
              handleSelectDatePreset={handleDatePresetChange(
                WorkDatePresetKey.WORK,
                'dueDatePreset',
              )}
            />
          </div>
          <div className={classes.filterContainer}>
            <InputDateRange
              label="Created Date"
              disabled={filtersDisabled}
              presetSelected={
                dateFilterPresetsCart?.[WorkDatePresetKey.WORK]
                  ?.createdDatePreset
              }
              startDateValue={getDateValue('createdDateStart')}
              handleStartChange={handleDateChange(
                'createdDateStart',
                DateRangeTerminator.START,
                dateFilterPresetsCart?.[WorkDatePresetKey.WORK]
                  ?.createdDatePreset,
              )}
              endDateValue={getDateValue('createdDateEnd')}
              handleEndChange={handleDateChange(
                'createdDateEnd',
                DateRangeTerminator.END,
                dateFilterPresetsCart?.[WorkDatePresetKey.WORK]
                  ?.createdDatePreset,
              )}
              handleSelectDatePreset={handleDatePresetChange(
                WorkDatePresetKey.WORK,
                'createdDatePreset',
              )}
            />
          </div>
          <div className={classes.filterContainer}>
            <InputDateRange
              label="Planned Start Date"
              disabled={filtersDisabled}
              presetSelected={
                dateFilterPresetsCart?.[WorkDatePresetKey.WORK]
                  ?.plannedStartDatePreset
              }
              startDateValue={getDateValue('plannedStartDateStart')}
              handleStartChange={handleDateChange(
                'plannedStartDateStart',
                DateRangeTerminator.START,
                dateFilterPresetsCart?.[WorkDatePresetKey.WORK]
                  ?.plannedStartDatePreset,
              )}
              endDateValue={getDateValue('plannedStartDateEnd')}
              handleEndChange={handleDateChange(
                'plannedStartDateEnd',
                DateRangeTerminator.END,
                dateFilterPresetsCart?.[WorkDatePresetKey.WORK]
                  ?.plannedStartDatePreset,
              )}
              handleSelectDatePreset={handleDatePresetChange(
                WorkDatePresetKey.WORK,
                'plannedStartDatePreset',
              )}
            />
          </div>
          <div className={classes.filterContainer}>
            <InputDateRange
              label="Start Date"
              disabled={filtersDisabled}
              presetSelected={
                dateFilterPresetsCart?.[WorkDatePresetKey.WORK]?.startDatePreset
              }
              startDateValue={getDateValue('startDateStart')}
              handleStartChange={handleDateChange(
                'startDateStart',
                DateRangeTerminator.START,
                dateFilterPresetsCart?.[WorkDatePresetKey.WORK]
                  ?.startDatePreset,
              )}
              endDateValue={getDateValue('startDateEnd')}
              handleEndChange={handleDateChange(
                'startDateEnd',
                DateRangeTerminator.END,
                dateFilterPresetsCart?.[WorkDatePresetKey.WORK]
                  ?.startDatePreset,
              )}
              handleSelectDatePreset={handleDatePresetChange(
                WorkDatePresetKey.WORK,
                'startDatePreset',
              )}
            />
          </div>
          <div className={classes.filterContainer}>
            <InputDateRange
              label="Completed Date"
              disabled={filtersDisabled}
              presetSelected={
                workOrdersInputCart?.dateFilterPresetsCart?.[
                  WorkDatePresetKey.WORK
                ]?.completionDatePreset
              }
              startDateValue={getDateValue('completionDateStart')}
              handleStartChange={handleDateChange(
                'completionDateStart',
                DateRangeTerminator.START,
                dateFilterPresetsCart?.[WorkDatePresetKey.WORK]
                  ?.completionDatePreset,
              )}
              endDateValue={getDateValue('completionDateEnd')}
              handleEndChange={handleDateChange(
                'completionDateEnd',
                DateRangeTerminator.END,
                dateFilterPresetsCart?.[WorkDatePresetKey.WORK]
                  ?.completionDatePreset,
              )}
              handleSelectDatePreset={handleDatePresetChange(
                WorkDatePresetKey.WORK,
                'completionDatePreset',
              )}
            />
          </div>
          <div
            className={clsx(classes.filtersDateRange, classes.filterContainer)}
          >
            <TextField
              value={convertMillisecondsToDays(
                workOrdersInputCart?.durationStart,
              )}
              onChange={handleDurationChange('durationStart')}
              label="Duration"
              type="number"
              disabled={filtersDisabled}
              variant="standard"
              inputLabelColor={colors.neutral.dim}
            />
            <span style={styles.separator}>to</span>
            <TextField
              value={convertMillisecondsToDays(
                workOrdersInputCart?.durationEnd,
              )}
              onChange={handleDurationChange('durationEnd')}
              label=""
              type="number"
              disabled={filtersDisabled}
              variant="standard"
            />
            <span style={styles.separator}>Days</span>
          </div>
          <div className={classes.filterContainer}>
            <Select
              multiple
              displayEmpty
              label="Priority"
              value={workOrdersInputCart?.priorityIds || []}
              onChange={handleMultiSelectChange('priorityIds')}
              renderValue={
                R.length(workOrdersInputCart?.priorityIds) > 0
                  ? null
                  : () => 'All'
              }
              disabled={filtersDisabled}
              inputLabelSx={inputLabelSx}
            >
              {workOrderPriorityUtilities.workOrderPriorityTypes.map(
                priority => (
                  <MenuItem key={priority.value} value={priority.value}>
                    {priority.label}
                  </MenuItem>
                ),
              )}
            </Select>
          </div>
          <div className={classes.filterContainer}>
            <Select
              multiple
              displayEmpty
              label="Status"
              value={workOrdersInputCart?.statusIds || []}
              onChange={handleMultiSelectChange('statusIds')}
              renderValue={
                R.length(workOrdersInputCart?.statusIds) > 0
                  ? null
                  : () => 'All'
              }
              disabled={filtersDisabled}
              inputLabelSx={inputLabelSx}
            >
              {workOrderStatusTypes.map(status => (
                <MenuItem key={+status.value} value={+status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </Select>
          </div>
          <div className={classes.filterContainer}>
            <WorkOrdersTemplateFolderFilter />
          </div>
          <div className={classes.filterContainer}>
            <WorkOrdersTemplateFilter />
          </div>
          {R.length(workOrdersInputCart?.workTemplateIds) === 1 && (
            <div className={classes.filterContainer}>
              <WorkOrdersWorkFieldsFilter />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkOrdersFiltersWorkSection;
